import { styled, alpha, lighten } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Chip,
  Box,
  Grid,
  Button,
  Paper,
} from '@mui/material';
import { colors, spacing } from '../../../styles/theme';

/** Cabecera de tarjeta con tinte de acento (claro / oscuro) */
export function infoCardHeaderSx(theme: Theme, accentRgb: string) {
  return {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.mode === 'dark' ? alpha(accentRgb, 0.22) : alpha(accentRgb, 0.1),
  };
}

/** Título de tarjeta con color de acento legible en ambos modos */
export function infoCardTitleSx(theme: Theme, accentHex: string) {
  return {
    color: theme.palette.mode === 'dark' ? lighten(accentHex, 0.42) : accentHex,
  };
}

// Header con gradiente (DetailModal global usa otro wrapper; esto queda por si se usa suelto)
export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: `${spacing.lg} ${spacing.xl}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
  color: theme.palette.common.white,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: `${spacing.lg} ${spacing.lg} 0 0`,
}));

export const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  '&:hover': {
    color: alpha(theme.palette.common.white, 0.85),
    backgroundColor: alpha(theme.palette.common.white, 0.1),
  },
}));

export const StyledDialogContent = styled(DialogContent)(() => ({
  padding: 0,
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  padding: spacing.xl,
}));

export const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: spacing.xl,
}));

export const ProductInfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.lg,
}));

export const ImageContainer = styled(Box)(() => ({
  position: 'relative',
}));

export const ProductImage = styled('img')(({ theme }) => ({
  width: '96px',
  height: '96px',
  objectFit: 'cover',
  borderRadius: spacing.sm,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

export const ProductDetails = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const ProductName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: '1.5rem',
  marginBottom: spacing.xs,
}));

export const SkuContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: spacing.xs,
}));

export const SkuLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginRight: spacing.sm,
}));

export const SkuValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

export const StatusChip = styled(Chip)(({ theme }) => ({
  '&.success': {
    backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.25 : 0.12),
    color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
  },
  '&.info': {
    backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.25 : 0.12),
    color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.dark,
  },
}));

export const StyledGrid = styled(Grid)(() => ({
  gap: spacing.lg,
}));

export const InfoCard = styled(Paper)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.06)
      : theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2),
  overflow: 'hidden',
}));

export const CardHeader = styled(Box)(() => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  display: 'flex',
  alignItems: 'center',
}));

export const CardTitle = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
}));

export const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const InfoGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginBottom: spacing.xs,
}));

export const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const DescriptionText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  lineHeight: 1.5,
}));

export const NoTagsText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
}));

export const TitleContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

export const TagsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: spacing.sm,
  marginTop: spacing.xs,
}));

export const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha('#7c3aed', theme.palette.mode === 'dark' ? 0.28 : 0.12),
  color: theme.palette.mode === 'dark' ? lighten('#7c3aed', 0.35) : '#7c3aed',
  fontSize: '12px',
  height: 24,
}));

export const PriceValue = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  '&.sale': {
    color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.main,
  },
  '&.cost': {
    color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.main,
  },
}));

export const StyledDialogActions = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.default : colors.grey[50],
  padding: `${spacing.lg} ${spacing.xl}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: spacing.md,
}));

export const CloseDialogButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.secondary,
  borderRadius: spacing.sm,
  fontWeight: 500,
  fontSize: '14px',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },
}));

export const EditButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
  color: theme.palette.common.white,
  borderRadius: spacing.sm,
  fontWeight: 500,
  fontSize: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.success.dark} 100%)`,
  },
}));

export const ConfirmDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: `${spacing.md} ${spacing.lg}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
  color: theme.palette.common.white,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '4px 4px 0 0',
  '& .MuiTypography-root': {
    fontSize: '1rem',
    fontWeight: 600,
  },
}));

export const ConfirmSaveButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.success.main} 100%)`,
  color: theme.palette.common.white,
  borderRadius: '4px',
  fontWeight: 500,
  fontSize: '14px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  textTransform: 'none',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.success.dark} 100%)`,
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
}));

export const ConfirmCancelButton = styled(Button)(({ theme }) => ({
  padding: `${spacing.sm} ${spacing.lg}`,
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.primary,
  borderRadius: '4px',
  fontWeight: 500,
  fontSize: '14px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
}));
